import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';

// Use direct axios call without auth interceptors for public endpoint
const API_BASE_URL = process.env.REACT_APP_API_URL || (typeof window !== 'undefined' && window.location.hostname === 'localhost' ? 'http://localhost:5000/api' : '/api');

const SharedChat = () => {
  const { shareToken } = useParams();
  const [conversation, setConversation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadSharedConversation = async () => {
      try {
        setLoading(true);
        // Use direct axios call without auth interceptors
        const res = await axios.get(`${API_BASE_URL}/public/ai/chat/${shareToken}`);
        setConversation(res.data?.conversation);
        if (!res.data?.conversation) {
          setError('Conversation data is empty');
        }
      } catch (err) {
        console.error('Failed to load shared conversation:', err);
        setError(err.response?.data?.message || 'Conversation not found or is no longer shared');
      } finally {
        setLoading(false);
      }
    };
    
    if (shareToken) {
      loadSharedConversation();
    } else {
      setError('No share token provided');
      setLoading(false);
    }
  }, [shareToken]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading conversation...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="text-6xl mb-4">🔒</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Conversation Not Available</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <p className="text-xs text-gray-400 mb-4">Token: {shareToken?.substring(0, 8)}...</p>
          <Link to="/" className="btn-primary inline-block px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">
            Go to Omabra
          </Link>
        </div>
      </div>
    );
  }

  // Handle case where conversation loaded but has no messages
  if (!conversation?.messages?.length) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="text-6xl mb-4">💬</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Empty Conversation</h1>
          <p className="text-gray-600 mb-6">This conversation doesn't have any messages yet.</p>
          <Link to="/" className="btn-primary inline-block px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">
            Go to Omabra
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-bold text-gray-900">{conversation?.title || 'Shared Conversation'}</h1>
              <div className="flex items-center gap-4 mt-1 text-sm text-gray-500">
                <span>Shared by: {conversation?.sharedBy || 'Anonymous'}</span>
                {conversation?.farmName && <span>• Farm: {conversation.farmName}</span>}
                <span>• {new Date(conversation?.createdAt).toLocaleDateString()}</span>
              </div>
            </div>
            <Link to="/" className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-semibold text-sm">
              <span>🐔</span>
              <span>Try Omabra</span>
            </Link>
          </div>
        </div>
      </header>

      {/* Chat Messages */}
      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="space-y-4">
          {(conversation?.messages || []).map((m, i) => (
            <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[80%]`}>
                {/* Show image if present */}
                {m.imageUrl && (
                  <div className={`mb-2 ${m.role === 'user' ? 'text-right' : 'text-left'}`}>
                    <img 
                      src={m.imageUrl} 
                      alt="Uploaded" 
                      className="inline-block max-h-64 rounded-lg border border-gray-200"
                    />
                  </div>
                )}
                <div className={`px-4 py-3 rounded-2xl shadow-sm ${
                  m.role === 'user' 
                    ? 'bg-primary-600 text-white rounded-br-md' 
                    : 'bg-white text-gray-800 rounded-bl-md border border-gray-100'
                }`}>
                  <div className="text-sm whitespace-pre-wrap">{m.content}</div>
                </div>
                <div className={`text-xs text-gray-400 mt-1 px-1 ${m.role === 'user' ? 'text-right' : 'text-left'}`}>
                  {m.timestamp ? new Date(m.timestamp).toLocaleString() : ''}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="mt-12 pt-6 border-t border-gray-200 text-center">
          <p className="text-gray-600 mb-4">
            This is a shared conversation from Omabra - AI-powered poultry farm management.
          </p>
          <Link to="/register" className="btn-primary inline-block">
            Get Started with Omabra
          </Link>
        </div>
      </div>
    </div>
  );
};

export default SharedChat;

